<template>
  <div class="licenses-card">
    <div class="licenses-container">

      <label for="licenseSearch" class="form-label">{{ $t('adm.label.license', 2) }}</label>
      <input id="licenseSearch" v-model="textFilter" class="form-control stihl-search-input"/>

      <stihl-filter-chips v-model="categoryFilter" :options="chipCategories"/>

      <hr>
      <div class="w-100 d-flex justify-content-end gap-2">
        <label class="checkbox-label">
          <input type="checkbox" @change="selectGroup('all', $event)" :disabled="isReadOnly"/>
          {{ $t('adm.label.selectAll') }}
        </label>
      </div>

      <stihl-accordion id="lic" :options="filteredAccordionCategories">
        <template v-slot:header-tech>
          <input id="grpTech"
                 type="checkbox"
                 @change="selectGroup('tech', $event)"
                 :disabled="isReadOnly"/>
        </template>
        <template v-slot:tech>
          <div class="license-group">
            <label v-for="lic in catTech" :key="lic.id" class="checkbox-label" :class="{ 'is-disabled': isReadOnly }">
              <input name="licenses"
                     type="checkbox"
                     :checked="hasLicense(lic.id)"
                     @change="select1(lic.id, $event)"
                     :disabled="isReadOnly"/>
              <span>{{ lic.displayName }}</span>
              <license-info :info="lic.description"/>
            </label>
          </div>
        </template>

        <template v-slot:header-academy>
          <input id="grpAcademy"
                 type="checkbox"
                 @click="selectGroup('academy', $event)"
                 :disabled="isReadOnly"/>
        </template>
        <template v-slot:academy>
          <div class="license-group">
            <label v-for="lic in catAcademy" :key="lic.id" class="checkbox-label"
                   :class="{ 'is-disabled': isReadOnly }">
              <input name="licenses"
                     type="checkbox"
                     :checked="hasLicense(lic.id)"
                     @change="select1(lic.id, $event)"
                     :disabled="isReadOnly"/>
              <span>{{ lic.displayName }}</span>
              <license-info :info="lic.description"/>
            </label>
          </div>
        </template>

        <template v-slot:header-business>
          <input id="grpB2B"
                 type="checkbox"
                 @click="selectGroup('business', $event)"
                 :disabled="isReadOnly"/>
        </template>
        <template v-slot:business>
          <div class="license-group">
            <label v-for="lic in catB2B" :key="lic.id" class="checkbox-label" :class="{ 'is-disabled': isReadOnly }">
              <input name="licenses"
                     type="checkbox"
                     :checked="hasLicense(lic.id)"
                     @change="select1(lic.id, $event)"
                     :disabled="isReadOnly"/>
              <span>{{ lic.displayName }}</span>
              <license-info :info="lic.description"/>
            </label>
          </div>
        </template>
      </stihl-accordion>

      <slot/>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import StihlFilterChips, { FilterChip } from '@/components/ui/StihlFilterChips.vue';
import StihlAccordion, { AccordionItem } from '@/components/ui/StihlAccordion.vue';
import { contains, containsStr, removeItem } from '@/components/utils';
import LicenseInfo from '@/components/ui/LicenseInfo.vue';
import { Logger } from 'oidc-client-ts';
import { LicenseCategory, LicenseData } from '@/components/backend';

export default defineComponent({
  name: 'LicensesEditor',
  components: { LicenseInfo, StihlAccordion, StihlFilterChips },
  emits: ['updateSelection'],
  props: {
    licenseDefinitions: { type: Array as PropType<LicenseData[]>, default: new Array<LicenseData>() },
    orgLicenses: { type: Array as PropType<string[]>, default: new Array<string>() },
    userLicenses: { type: Array as PropType<string[]>, default: new Array<string>() },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      textFilter: '',
      categoryFilter: 'all',
      selected: this.filterAllowedLicense(this.userLicenses),
    };
  },
  methods: {

    /**
     * filter licenses - in case the user has received a license that is not available to his organization
     */
    filterAllowedLicense(userLicenses: string[]): string[] {
      return this.orgLicenses?.filter((lic) => contains(userLicenses, lic)) ?? [];
    },

    select1(lic: string, e: Event) {
      const { checked } = (e.target as HTMLInputElement);
      Logger.debug('lic', 'select1: ', lic, checked);

      const newSelection = this.selected;
      if (checked) {
        newSelection.push(lic);
      } else {
        removeItem(newSelection, lic);
      }
      this.$emit('updateSelection', newSelection);
    },

    selectGroup(cat: LicenseCategory | 'all', e: Event) {
      const { checked } = (e.target as HTMLInputElement);
      Logger.debug('lic', 'selectGroup: ', cat, checked);

      const newSelection = this.selected;
      const licenseOptions = this.fromCategory(cat);
      if (checked) {
        licenseOptions?.forEach((lic) => {
          if (!contains(this.selected, lic.id)) newSelection.push(lic.id);
        });
      } else {
        licenseOptions?.forEach((lic) => {
          removeItem(newSelection, lic.id);
        });
      }
      this.$emit('updateSelection', newSelection);
    },

    fromCategory(cat: string): LicenseData[] | undefined {
      return cat === 'all' && this.categoryFilter === 'all'
          ? this.visibleLicenses
          : this.visibleLicenses?.filter((x: LicenseData) => x.category === cat || x.category === this.categoryFilter);
    },

    hasLicense(lic: string): boolean {
      // Logger.debug(`[lic] hasLicense(${lic}):`, this.selected?.indexOf(lic) >= 0);
      return this.selected?.indexOf(lic) >= 0;
    },
  },
  computed: {

    visibleLicenses(): LicenseData[] | undefined {
      return this.licenseDefinitions?.filter((lic) => containsStr(lic.displayName, this.textFilter));
    },

    chipCategories(): FilterChip[] {
      return [
        { key: 'all', label: this.$t('adm.label.all') },
        { key: 'tech', label: this.$t('adm.label.licenceCategory.tech') },
        { key: 'academy', label: this.$t('adm.label.licenceCategory.academy') },
        { key: 'business', label: this.$t('adm.label.licenceCategory.b2b') },
      ];
    },
    filteredAccordionCategories(): AccordionItem[] {
      if (this.categoryFilter === 'all') return this.accordionCategories;
      return this.accordionCategories.filter((c) => this.categoryFilter === c.key);
    },
    accordionCategories(): AccordionItem[] {
      return [
        { key: 'tech', header: this.$t('adm.label.licenceCategory.tech') },
        { key: 'academy', header: this.$t('adm.label.licenceCategory.academy') },
        { key: 'business', header: this.$t('adm.label.licenceCategory.b2b') },
      ] as AccordionItem[];
    },
    catTech(): LicenseData[] | undefined {
      return this.fromCategory('tech');
    },
    catAcademy(): LicenseData[] | undefined {
      return this.fromCategory('academy');
    },
    catB2B(): LicenseData[] | undefined {
      return this.fromCategory('business');
    },
  },
  mounted() {
    Logger.debug('lic', 'licenses: ', this.selected.length, ' of ', this.orgLicenses?.length);
  },
});
</script>

<style>

.licenses-card {
  float: right;
  width: 50rem;
  right: 0;
  flex-shrink: 0;
  background: var(--grey-ultra-light, #F8F8F8);
}

.licenses-card .licenses-container {
  display: flex;
  width: 35rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;

  margin: 4.5rem 7.5rem;
}

.licenses-card .stihl-search-input {
  position: relative;
  width: 100%;
  height: 48px;
  padding: 1rem 1rem 1rem 2.5rem;
  outline: none;

  color: var(--grey-dark, #505050);
  font-feature-settings: 'clig' off, 'liga' off;

  font-family: var(--stihl-font-family-text);
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;

  background-image: url('@/assets/img/icons/magnifying-glass.svg');
  background-repeat: no-repeat;
  background-size: 1rem;
  background-position-x: 1rem;
  background-position-y: 1rem;

  z-index: 5;
}

.licenses-card .license-group {
  display: flex;
  flex-direction: column;
  gap: .25rem;
}

label.checkbox-label {
  display: flex;
  align-items: center;
  gap: .5rem;

  color: var(--grey-deep);
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 14px;
  line-height: 2rem;

  padding: 0;
  cursor: pointer;
  user-select: none;
}

label.checkbox-label.is-disabled {
  color: #00000080;
}

input[type="checkbox"]:not([role="switch"]):disabled:active, input[type="radio"]:disabled:active {
  outline: none !important;
}
</style>
